import type { Dispatch, ReactNode } from "react";
import React, { createContext, useContext, useReducer } from "react";
import { Tearsheet } from "@procore/core-react";
import { CreateProjectTearsheet } from "./create-project-tearsheet-content";
import { ProjectDetailTearsheet } from "./project-detail-tearsheet-content";
import { LaborPlansContainer } from "../../labor-plans/labor-plans-container";
import type { TableApi } from "@procore/data-table";
import { useGetCustomFields, useGetIntegratedFields } from "../../common/queries/queries";
import { CustomFieldEntity } from "@laborchart-modules/common/dist/rethink/schemas/enums/custom-fields";
import { AssignmentTearsheetContent } from "../assignment/assignment-tearsheet-content";
import { AssignmentTearsheetProvider } from "../assignment/assignment-tearsheet";
import { CustomizeProjectAlertsTearsheetContent } from "./customize-project-alerts-tearsheet-content";
import type { CannedMessage } from "@laborchart-modules/common";
import type { CannedMessageType } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import type { CreateAssignmentAction } from "../assignment/assignment-tearsheet";

type ProjectTearsheetState = OpenState | ClosedState;
type OpenState = { isOpen: true } & (
   | CreateProject
   | ProjectDetail
   | CreateLaborPlan
   | CreateAssignment
   | CustomizeProjectAlerts
);
type ClosedState = { isOpen: false; show: null };
type CreateProject = { show: "create-project"; callback?: (data?: any) => void };
type ProjectDetail = {
   show: "project-detail";
   projectId: string;
   showActiveTab?: string;
   callback?: (data?: any) => void;
};
type CreateLaborPlan = {
   show: "create-labor-plan";
   projectId: string;
   callback?: (data?: any) => void;
};
type CreateAssignment = {
   show: "create-assignment";
   projectId?: string;
   categoryId?: string;
   subcategoryId?: string;
   callback?: (data?: any) => void;
};
type CustomizeProjectAlerts = {
   show: "customize-assignment-alerts";
   projectId: string;
   alertType: CannedMessageType;
   projectRoles: any[];
   cannedMessage?: CannedMessage;
};

type CustomizeProjectAlertsAction = {
   type: "customize-assignment-alerts";
   projectId: string;
   alertType: CannedMessageType;
   projectRoles: any[];
   cannedMessage?: CannedMessage;
};
type CreateProjectAction = {
   type: "open-create-project";
   callback?: (data: any) => void;
};
type ProjectDetailAction = {
   type: "open-project-detail";
   projectId: string;
   showActiveTab?: string;
   callback?: (data: any) => void;
};
type CreateLaborPlanAction = {
   type: "create-labor-plan";
   projectId: string;
   callback?: (data: any) => void;
};
type CloseAction = { type: "close" };

export type ProjectTearsheetActions =
   | CreateProjectAction
   | ProjectDetailAction
   | CloseAction
   | CreateLaborPlanAction
   | CreateAssignmentAction
   | CustomizeProjectAlertsAction;

function reducer(
   _state: ProjectTearsheetState,
   action: ProjectTearsheetActions,
): ProjectTearsheetState {
   switch (action.type) {
      case "open-create-project": {
         return {
            isOpen: true,
            show: "create-project",
            callback: action.callback,
         };
      }
      case "open-project-detail": {
         return {
            isOpen: true,
            show: "project-detail",
            projectId: action.projectId,
            showActiveTab: action.showActiveTab,
            callback: action.callback,
         };
      }
      case "create-labor-plan": {
         return {
            isOpen: true,
            show: "create-labor-plan",
            projectId: action.projectId,
            callback: action.callback,
         };
      }
      case "open-create-assignment": {
         return {
            isOpen: true,
            show: "create-assignment",
            projectId: action.projectId,
            categoryId: action.categoryId ?? undefined,
            subcategoryId: action.subcategoryId ?? undefined,
            callback: action.callback,
         };
      }
      case "customize-assignment-alerts": {
         return {
            isOpen: true,
            show: "customize-assignment-alerts",
            projectId: action.projectId,
            alertType: action.alertType,
            cannedMessage: action.cannedMessage,
            projectRoles: action.projectRoles,
         };
      }
      case "close": {
         return {
            isOpen: false,
            show: null,
         };
      }
   }
}

const initialState: ProjectTearsheetState = {
   isOpen: false,
   show: null,
};

const ProjectTearsheetContext = createContext<
   (ProjectTearsheetState & { dispatch: Dispatch<ProjectTearsheetActions> }) | null
>(null);

export function ProjectTearsheetProvider({
   children,
   projectsTableApi,
}: {
   children: ReactNode;
   projectsTableApi?: TableApi;
}) {
   const [state, dispatch] = useReducer(reducer, initialState);
   return (
      <ProjectTearsheetContext.Provider value={{ ...state, dispatch }}>
         {children}
         <ProjectTearsheet projectsTableApi={projectsTableApi} />
      </ProjectTearsheetContext.Provider>
   );
}

export function useProjectTearsheet() {
   const context = useContext(ProjectTearsheetContext);

   if (!context) {
      throw Error(
         "'useProjectTearsheet' is supposed to be used in the component that is a child of 'ProjectTearsheetProvider'.",
      );
   }

   return context;
}

export function ProjectTearsheet(props: { projectsTableApi?: TableApi }) {
   const state = useProjectTearsheet();
   const isProjectDetailOpen = state.show === "project-detail";
   const isCreateProjectOpen = state.show === "create-project";
   const isCreateLaborPlan = state.show === "create-labor-plan";
   const isCreateAssignmentOpen = state.show === "create-assignment";
   const isCustomizeProjectAlertsOpen = state.show === "customize-assignment-alerts";
   const { data: customFields } = useGetCustomFields(CustomFieldEntity.PROJECT);

   const { data: integratedFields } = useGetIntegratedFields();

   const handleClose = () => {
      if ("callback" in state) {
         state.callback?.();
      }
      state.dispatch({ type: "close" });
   };
   const onProjectCreated = (projectId: string) => {
      state.dispatch({ type: "open-project-detail", projectId });
      props.projectsTableApi?.refreshServerSide({});
   };

   const onUpdateProject = () => props.projectsTableApi?.refreshServerSide({});

   const onCreateLaborPlanClick = (projectId: string, callback: any) => {
      state.dispatch({ type: "create-labor-plan", projectId, callback });
   };
   const onShowProjectDetail = (projectId: string, showActiveTab?: string) => {
      showActiveTab
         ? state.dispatch({ type: "open-project-detail", projectId, showActiveTab })
         : state.dispatch({ type: "open-project-detail", projectId });
   };
   return (
      <Tearsheet open={state.isOpen} onClose={handleClose}>
         {isCreateProjectOpen && (
            <CreateProjectTearsheet
               onClose={handleClose}
               onProjectCreated={onProjectCreated}
               callback={state.callback}
            />
         )}
         {isProjectDetailOpen && (
            <ProjectDetailTearsheet
               projectId={state.projectId}
               handleCreateLaborPlanClick={onCreateLaborPlanClick}
               onUpdateProject={onUpdateProject}
               customFields={customFields!}
               integratedFields={integratedFields!}
               showActiveTab={state.showActiveTab}
               callback={state.callback}
            />
         )}
         {isCreateLaborPlan && (
            <LaborPlansContainer
               projectId={state.projectId}
               showProjectDetail={onShowProjectDetail}
               tearsheet
               callback={state.callback}
            />
         )}
         {isCreateAssignmentOpen && (
            <AssignmentTearsheetProvider assignmentTableApi={undefined} onClose={handleClose}>
               <AssignmentTearsheetContent
                  projectId={state.projectId}
                  categoryId={state.categoryId}
                  subcategoryId={state.subcategoryId}
                  onClose={handleClose}
                  callback={state.callback}
               />
            </AssignmentTearsheetProvider>
         )}
         {isCustomizeProjectAlertsOpen && (
            <CustomizeProjectAlertsTearsheetContent
               projectId={state.projectId}
               onShowProjectDetail={onShowProjectDetail}
               cannedMessage={state.cannedMessage as CannedMessage}
               alertType={state.alertType}
               projectRoles={state.projectRoles}
            />
         )}
      </Tearsheet>
   );
}

export function onCreateProjectClick(dispatch: Dispatch<ProjectTearsheetActions>) {
   dispatch({ type: "open-create-project" });
}

export function onProjectDetailClick(
   dispatch: Dispatch<ProjectTearsheetActions>,
   id: string,
   showActiveTab?: string,
   callback?: (data?: any) => void,
) {
   dispatch({ type: "open-project-detail", projectId: id, showActiveTab, callback });
}
export function onCreateAssignmentClick(
   dispatch: Dispatch<ProjectTearsheetActions>,
   projectId?: string,
   categoryId?: string,
   subcategoryId?: string,
) {
   dispatch({ type: "open-create-assignment", projectId, categoryId, subcategoryId });
}

export function onCustomizeProjectAlertsClick(
   dispatch: Dispatch<ProjectTearsheetActions>,
   id: string,
   alertType: CannedMessageType,
   projectRoles: any[],
   cannedMessage: CannedMessage,
) {
   dispatch({
      type: "customize-assignment-alerts",
      projectId: id,
      alertType: alertType,
      projectRoles: projectRoles,
      cannedMessage: cannedMessage,
   });
}
